import { DateTime } from 'luxon'

import { DateTimeFieldAccuracy, DateTimeFieldBehaviour, DateTimeFieldFormat } from '../../definitions/enums'
import { DateTimeFieldDefinition } from '../../definitions/field-definitions'
import {
  getJSDateFromDateTime,
  getNormalizedDateValue,
  getStringDateFromDateTime,
  getStringFromDateTime,
} from '../../utils/date-time'
import { FieldValue } from './field.class'

export class DateTimeValue extends FieldValue<DateTime> {
  accuracy: DateTimeFieldAccuracy
  kind: DateTimeFieldBehaviour = DateTimeFieldBehaviour.Local
  format: DateTimeFieldFormat

  constructor(
    date?: Date | DateTime | string,
    accuracy?: DateTimeFieldAccuracy,
    kind?: DateTimeFieldBehaviour,
    format?: DateTimeFieldFormat,
  ) {
    super()
    this.accuracy = accuracy ?? DateTimeFieldAccuracy.Date
    this.kind = kind ?? DateTimeFieldBehaviour.Local
    this.format = format ?? DateTimeFieldFormat.Default

    this.value = getNormalizedDateValue(date)
  }

  public toJSON() {
    const { value: _, kind, ...rest } = this
    const v = _ as DateTime
    let value = null

    if (v && kind == DateTimeFieldBehaviour.Utc) {
      value = v.toISO()
    }

    // this is needed to strip the timezone from the date-time string to make the server save the date-time as it is
    // i.e. without any timezone conversion
    if (v && kind == DateTimeFieldBehaviour.Local) {
      // forcing 'en' we are pretty sure that no-numeric dates (ex. ar-EG) are correctly parsed and sent
      value = v.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS", { locale: 'en' })
    }

    return { ...rest, value, kind }
  }

  public mapField(field: DateTimeValue, fieldDef: DateTimeFieldDefinition): void {
    this.kind = fieldDef.behaviour
    this.format = fieldDef.format
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.accuracy = DateTimeFieldAccuracy[field.accuracy] as any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const date = (<any>field.value) as string
    //all initialised dates will be normalized to the BU timezone for checks and comparisons
    this.value = getNormalizedDateValue(date)
  }

  public toStringForView(locale?: string, allowUnknown?: boolean): string {
    let valueToStringify = this.value
    if (locale) {
      valueToStringify = (this.value as DateTime)?.setLocale(locale)
    }
    return getStringFromDateTime(valueToStringify as DateTime, {
      accuracy: this.accuracy,
      format: this.format,
    })
  }

  public toISO(): string | null {
    return this.value ? getStringDateFromDateTime(this.value) : null
  }

  public toJSDate(): Date | null {
    return this.value ? getJSDateFromDateTime(this.value) : null
  }

  public isValidDate(): boolean {
    if (this.value && this.value.isValid && this.value.year >= 1900 && this.value.year <= 2099) {
      return true
    }

    return false
  }
}
